import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  MDBCardGroup,
  MDBPagination,
  MDBPaginationItem,
  MDBPaginationLink,
  MDBRow,
  MDBSpinner,
} from "mdb-react-ui-kit";
import CommitteeCard from "./CommitteeCard";
import defaultCommitteePicture from "../img/defaultcommitteepic.jpg";
import { parseISO } from "date-fns";
import getCommitteesMeetings from "../utils/getCommitteesMeetings";
import fetchRelatedBills from "../utils/fetchRelatedBills";
import { matchesMeetingsByDateRange } from "../utils/FilterMeetingsByDates";
import fetchBillsWithHearings from "../utils/fetchBillsWithHearings";
import fetchBillsWithSponsors from "../utils/fetchBillsWithSponsors";
import { COMMITTEE_TYPE_LABELS } from "../utils/committeeTypeLabels";

function CommitteeList({
  searchTerm,
  filterSession,
  filterLegislature,
  // committeeType,
  setCommittees,
  committees,
  setFilteredCommittees,
  filteredCommittees,
  setRelevantBills,
  relevantBills,
  dateRange,
  selectedLegislators,
  selectedChambers,
  selectedBills,
  selectedBillTypes,
  selectedBillStatus,
  setLoadingExtraData,
  selectedCommittees,
  selectedCommitteeTypes,
  cookiesLoaded
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;
  const [committeesUpdated, setCommitteesUpdated] = useState(false);
  const [referredStatusList] = useState([
    "(H) Referred to Committee",
    "(H) Rereferred to Committee",
    "(H) Taken from 2nd Reading: Rereferred to Committee",
    "(H) Referred to Committee to Consider Senate Amendments",
    "(S) Referred to Committee",
    "(S) Rereferred to Committee",
    "(S) Taken from 2nd Reading: Rereferred to Committee",
    "(S) Referred to Committee to Consider House Amendments",
  ]);

  const isBillDateInRange = (billDate, dateRange) => {
    return dateRange
      ? matchesMeetingsByDateRange(billDate, dateRange)
      : new Date(billDate) >= new Date().setHours(0, 0, 0);
  };
  const compareBills = (billFilter, bill, bypassBills) => {
    return (
      bill.billNumber === billFilter.billNumber ||
      (bypassBills &&
        bypassBills.some(
          (bypassBill) => bill.billNumber.split(" ")[0] === bypassBill.enum
        ))
    );
  };
  const compareBillTypes = (billFilter, bill, bypassBills) => {
    return (
      billFilter.enum === bill.billNumber.split(" ")[0] ||
      (bypassBills &&
        bypassBills.some(
          (bypassBill) => bypassBill.billNumber === bill.billNumber
        ))
    );
  };

  useEffect(() => {
    setLoading(true);
    const fetchCommittees = async () => {
      try {
        const [standingCommittees, nonStandingCommittees, committeesImages] =
          await Promise.all([
            axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/findBySessionId?sessionId=${filterSession.id}`
            ),
            axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/nonStandingCommittees/findByLegislatureId?legislatureId=${filterLegislature.id}`
            ),
            (async () => {
              try {
                const response = await axios.get(
                  `${process.env.REACT_APP_WP_HOST}/wp-json/legmt/v1/committees`
                );
                return response.data;
              } catch (error) {
                console.warn("WordPress API Failed:", error.message);
                return [];
              }
            })(),
          ]);

        // **FILTER nonStandingCommittees to only include those with future member endDates**
        const today = new Date();
        const filteredNonStandingCommittees = nonStandingCommittees.data.filter(
          (committee) =>
            committee.memberships &&
            committee.memberships.some(
              (member) => new Date(member.endDate) > today
            )
        );

        setCommittees([
          ...standingCommittees.data,
          ...filteredNonStandingCommittees,
        ]);
        setImages(committeesImages);
        setLoading(false);
        const standingCommitteeIds = standingCommittees.data.map(
          (committee) => committee.id
        );
        const standingCommitteeMeetings = await getCommitteesMeetings(
          standingCommitteeIds,
          "standing"
        );

        let updatedStandingCommittees = standingCommittees.data.map(
          (committee) => {
            const meetings = standingCommitteeMeetings
              .filter(
                (item) =>
                  item.standingCommittee &&
                  item.standingCommittee.id === committee.id
              )
              .map((item) => ({
                meetingTime: item.meetingTime,
                location: item.location,
                bills: [],
              }));

            return {
              ...committee,
              meetingTimes: meetings.length > 0 ? meetings : [],
              referredBills: [],
              currentBills: [],
            };
          }
        );

        const relatedBills = await fetchRelatedBills({
          // ToDo: Rewrite this to allow for a reversal in ordering of API calls
          committeeIds: standingCommitteeIds,
          billStatus: false,
          sessionIds: [filterSession.id],
        });
        setRelevantBills(relatedBills);
        const billsWithHearings = await fetchBillsWithHearings(relatedBills);
        const billsWithSponsors = await fetchBillsWithSponsors(
          billsWithHearings
        );
        // add bills to their committees
        billsWithSponsors.forEach((bill) => {
          if (bill.draft && bill.draft.billStatuses) {
            const referredStatuses = bill.draft.billStatuses.filter(
              (billStatus) =>
                referredStatusList.some(
                  (rStatus) => billStatus.billStatusCode.name === rStatus
                )
            );

            referredStatuses.sort((rStatus) => rStatus.timeStamp).reverse();

            referredStatuses.forEach((rStatus, index) => {
              const committee = updatedStandingCommittees.find(
                (committee) =>
                  committee.id === rStatus.standingCommitteeId &&
                  !committee.referredBills.some(
                    (rBill) =>
                      rBill.billNumber ===
                      `${bill.billType.code} ${bill.billNumber}`
                  )
              );
              const prevCommitteeStatus = referredStatuses
                .slice(index + 1)
                .find(
                  (prevStatus) =>
                    prevStatus.standingCommitteeId !==
                    rStatus.standingCommitteeId
                );

              if (committee && index === 0) {
                committee.currentBills.push({
                  billNumber: `${bill.billType.code} ${bill.billNumber}`,
                  statusDate: new Date(rStatus.timeStamp).setHours(0, 0, 0),
                  previousCommittee: prevCommitteeStatus
                    ? prevCommitteeStatus.standingCommitteeId
                    : null,
                });
              }
              if (committee) {
                committee.referredBills.push({
                  billNumber: `${bill.billType.code} ${bill.billNumber}`,
                  statusDate: new Date(rStatus.timeStamp).setHours(0, 0, 0),
                  previousCommittee: prevCommitteeStatus
                    ? prevCommitteeStatus.standingCommitteeId
                    : null,
                });
              }
            });
          }

          const committeeIds = [
            ...new Set(
              bill.hearingDetails.map(
                (hearingDetail) => hearingDetail.committeeId
              )
            ),
          ];

          committeeIds.forEach((committeeId) => {
            const committee = updatedStandingCommittees.find(
              (committee) => committee.id === committeeId
            );

            if (committee) {
              bill.hearingDetails.forEach((hearingDetail) => {
                const meeting = committee.meetingTimes.find(
                  (meeting) =>
                    parseISO(meeting.meetingTime).getTime() ===
                      parseISO(hearingDetail.meetingTime).getTime() &&
                    hearingDetail.committeeId === committee.id
                );

                if (meeting) {
                  meeting.bills.push({
                    shortTitle: bill.draft.shortTitle || "N/A",
                    sponsorId: bill.sponsorId,
                    requesterId: bill.draft.requesterId,
                    requesterType: bill.draft.requesterType,
                    sponsor: bill.sponsor
                      ? `${bill.sponsor.lastName}, ${bill.sponsor.firstName}`
                      : "N/A",
                    billNumber:
                      `${bill.billType.code} ${bill.billNumber}` || "N/A",
                  });
                }
              });
            }
          });
        });

        setCommittees([
          ...updatedStandingCommittees,
          ...filteredNonStandingCommittees,
        ]);
        //setImages(committeesImages);
        setCommitteesUpdated(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    if (filterSession && filterLegislature) {
      fetchCommittees();
    }
  }, [filterSession, filterLegislature]);

  useEffect(() => {
    if (committeesUpdated) {
      setLoadingExtraData(false);
      setCommitteesUpdated(false);
    }
  }, [committees]);

  useEffect(() => {
    const filtered = committees.filter((committee) => {
      // Get the committee code and apply transformation if it's "BIBC"
      const committeeCode = committee.committeeDetails?.committeeCode?.code;
      if (!committeeCode) return false;

      const transformedCode =
        committeeCode === "BIBC" ? "IBC-B" : committeeCode;

      const matchesSearch =
        transformedCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        committee.committeeDetails.committeeCode.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // const matchesCommitteeType =
      //   committeeType.length > 0
      //     ? committeeType.includes(
      //         committee.committeeDetails.committeeCode.committeeType.description
      //       )
      //     : true;

      const matchesCommitteeTypes =
        selectedCommitteeTypes.length > 0
          ? selectedCommitteeTypes.includes(
              committee.committeeDetails.committeeCode.committeeType.code
            )
          : true;

      const matchesSelectedCommittee =
        selectedCommittees &&
        selectedCommittees.length > 0
          ? selectedCommittees.includes(committee.id)
          : true;

      const matchesMeetingsDates =
        !dateRange ||
        (committee.meetingTimes &&
          committee.meetingTimes.some((meetingTime) =>
            matchesMeetingsByDateRange(meetingTime.meetingTime, dateRange)
          ));

      const matchesLegislators =
        !selectedLegislators.legs ||
        (selectedLegislators.legs &&
          selectedLegislators.legs.length === 0) ||
        (selectedLegislators.roles &&
          selectedLegislators.roles.length === 0) ||
        (() => {
          const legislatorIds = selectedLegislators.legs.map(
            (legislator) => legislator.id
          );

          const isSponsor =
            selectedLegislators.roles.includes("Sponsor") &&
            committee.meetingTimes?.some((meeting) =>
              meeting.bills?.some((bill) =>
                legislatorIds.includes(bill.sponsorId)
              )
            );

          const isRequester =
            selectedLegislators.roles.includes("Requester") &&
            committee.meetingTimes?.some((meeting) =>
              meeting.bills?.some(
                (bill) =>
                  bill.draft?.requesterType === "LEGISLATOR" &&
                  legislatorIds.includes(bill.draft.requesterId)
              )
            );

          const isMember =
            selectedLegislators.roles.includes("Member") &&
            committee.memberships?.some((membership) =>
              legislatorIds.includes(membership.legislatorId)
            );

          return isSponsor || isRequester || isMember;
        })();

      const matchesChambers =
        selectedChambers && (
          !selectedChambers.length ||
            selectedChambers.some((chamber) => chamber.enum === committee.chamber));

      const billTypesThatBypassSelected = selectedBillTypes.filter(
        (billFilter) =>
          !selectedBills.some(
            (bill) => billFilter.enum === bill.billNumber.split(" ")[0]
          )
      );

      const matchesBillHearings = selectedBills.some(
        (billFilter) =>
          committee.meetingTimes &&
          committee.meetingTimes.some(
            (meetingTime) =>
              meetingTime.bills.some((bill) =>
                compareBills(billFilter, bill, billTypesThatBypassSelected)
              ) && isBillDateInRange(meetingTime.meetingTime, dateRange)
          )
      );

      const matchesBillsReferred = dateRange
        ? selectedBills.some(
            (billFilter) =>
              committee.referredBills &&
              committee.referredBills.some(
                (bill) =>
                  compareBills(billFilter, bill, billTypesThatBypassSelected) &&
                  isBillDateInRange(bill.statusDate, dateRange)
              )
          )
        : selectedBills.some(
            (billFilter) =>
              committee.currentBills &&
              committee.currentBills.some((bill) =>
                compareBills(billFilter, bill, billTypesThatBypassSelected)
              )
          );

      const matchesBills =
        !selectedBills.length ||
        (selectedBillStatus
          ? matchesBillHearings
          : matchesBillHearings || matchesBillsReferred);

      const billsThatBypassType = selectedBills.filter(
        (bill) =>
          !selectedBillTypes.some(
            (billFilter) => billFilter.enum === bill.billNumber.split(" ")[0]
          )
      );

      // Bill Type Filtering logic in case it is wanted later.
      const matchesBillTypes =
        !selectedBillTypes.length ||
        selectedBillTypes.some((billFilter) =>
          selectedBillStatus
            ? committee.meetingTimes &&
              committee.meetingTimes.some(
                (meetingTime) =>
                  meetingTime.bills.some((bill) =>
                    compareBillTypes(billFilter, bill, billsThatBypassType)
                  ) && isBillDateInRange(meetingTime.meetingTime, dateRange)
              )
            : dateRange
            ? committee.referredBills &&
              committee.referredBills.some(
                (bill) =>
                  compareBillTypes(billFilter, bill, billsThatBypassType) &&
                  isBillDateInRange(bill.statusDate, dateRange)
              )
            : committee.currentBills &&
              committee.currentBills.some((bill) =>
                compareBillTypes(billFilter, bill, billsThatBypassType)
              )
        );

      return (
        matchesSearch &&
        // matchesCommitteeType &&
        matchesCommitteeTypes &&
        matchesMeetingsDates &&
        matchesLegislators &&
        matchesChambers &&
        matchesBills &&
        matchesBillTypes &&
        matchesSelectedCommittee
      );
    });

    filtered.sort((a, b) => {
      const nameA = a.committeeDetails.committeeCode.name
        .replace(/^\(S\)\s|\(H\)\s/, "")
        .toLowerCase();
      const nameB = b.committeeDetails.committeeCode.name
        .replace(/^\(S\)\s|\(H\)\s/, "")
        .toLowerCase();
      return nameA.localeCompare(nameB);
    });
    setFilteredCommittees(filtered);
    setCurrentPage(1); // Reset to the first page after filtering
  }, [
    committees,
    searchTerm,
    // committeeType,
    selectedCommitteeTypes,
    dateRange,
    setFilteredCommittees,
    cookiesLoaded,
    selectedLegislators,
    selectedChambers,
    selectedBills,
    selectedBillTypes,
    selectedBillStatus,
    selectedCommittees,
  ]);

  // Helper to compute canonical unique key (used for filtering)
  const computeCommitteeKey = (committee) =>
    `${committee.sessionId ? "standing" : "nonStanding"}-${committee.id}`;

  // Helper to remove (H) or (S) from the beginning of the committee name
  const cleanCommitteeName = (committee) =>
    committee.committeeDetails.committeeCode.name
      .replace(/^\((H|S)\)\s*/, "")
      .toLowerCase();

  // Filtering & Sorting Committees
  useEffect(() => {
    const filtered = committees.filter((committee) => {
      // Using the canonical key for matching selected committees
      const committeeKey = computeCommitteeKey(committee);
      const matchesSelectedCommittee =
        selectedCommittees &&
          selectedCommittees.length > 0
            ? selectedCommittees.includes(committeeKey)
            : true;

      const matchesCommitteeTypes =
        selectedCommitteeTypes &&
          selectedCommitteeTypes.length > 0
            ? selectedCommitteeTypes.includes(
                committee.committeeDetails.committeeCode.committeeType.code
              )
            : true;

      // Extract both the committee name and code
      const committeeName =
        committee.committeeDetails?.committeeCode?.name?.toLowerCase() || "";
      const committeeCode =
        committee.committeeDetails?.committeeCode?.code?.toLowerCase() || "";

      // Convert search term to lowercase for case-insensitive comparison
      const lowerCaseSearch = searchTerm.toLowerCase();

      // Check if the search term appears in either the name or the code
      const matchesSearch =
        committeeName.includes(lowerCaseSearch) ||
        committeeCode.includes(lowerCaseSearch);

      return matchesSearch && matchesSelectedCommittee && matchesCommitteeTypes;
    });

    // Sort the filtered committees alphabetically by cleaned name
    const sorted = filtered.slice().sort((a, b) => {
      return cleanCommitteeName(a).localeCompare(cleanCommitteeName(b));
    });

    setFilteredCommittees(sorted);
    setCurrentPage(1);
  }, [committees, searchTerm, selectedCommittees, setFilteredCommittees]);

  const paginatedCommittees = () => {
    return filteredCommittees.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  };

  const handlePageChange = (page) => setCurrentPage(page);

  // For rendering cards, we can still use a unique key that includes the index if needed,
  // but for filtering, we use the canonical key.
  const generateCommitteeCardKey = (committee, index) =>
    `${computeCommitteeKey(committee)}-${index}`;

  // Helper to get the featured image
  const getFeaturedImage = (committee) => {
    const type = committee.sessionId ? "StandingCommittee" : "NonStandingCommittee";
    const match = images.content.find(
        (image) =>
            image.committeeType === type &&
            image.lawsId === committee.id
    );

    return (match && match.photos && match.photos.legmt_committee_tiny) ?
      match.photos.legmt_committee_tiny :
        defaultCommitteePicture;
  };

  // Helper for committee code and title case conversion remain the same.
  const getCommitteeCode = (committee) => {
    const committeeCode = committee.committeeDetails.committeeCode.code;
    return committeeCode === "BIBC" ? "IBC-B" : committeeCode;
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // ---- New: Class helpers for footer parts ----
  const getFooterLeftClass = (committee) => {
    // Use an empty string if committee.chamber is undefined.
    const chamber = (committee.chamber || "").toLowerCase();
    let chamberClass = "default";
    if (chamber === "house") {
      chamberClass = "house";
    } else if (chamber === "senate") {
      chamberClass = "senate";
    } else if (chamber === "joint") {
      chamberClass = "joint";
    }
    return `footer-left ${chamberClass}`;
  };

  const getFooterRightClass = (committee) => {
    const typeClass = committee.sessionId ? "standing" : "nonstanding";
    return `footer-right ${typeClass}`;
  };

  const getFooterLeftLabel = (committee) => {
    if (committee.sessionId) return toTitleCase(committee.chamber);

    const typeCode =
      committee.committeeDetails?.committeeCode?.committeeType?.code;

    if (!typeCode || typeCode === "IC") return "";

    return COMMITTEE_TYPE_LABELS[typeCode] || "";
  };

  if (loading)
    return (
      <div className="d-flex justify-content-center mt-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );

  return (
    <>
      <MDBRow className="row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 row-cols-xl-6 g-2 mt-0 mb-2 justify-content-center">
        {paginatedCommittees().map((committee, index) => {
          const canonicalKey = computeCommitteeKey(committee);
          const cardKey = generateCommitteeCardKey(committee, index);
          return (
            <MDBCardGroup key={cardKey}>
              <CommitteeCard
                committeeCode={getCommitteeCode(committee)}
                committeeTitle={committee.committeeDetails.committeeCode.name}
                imageUrl={getFeaturedImage(committee)}
                footerLeft={getFooterLeftLabel(committee)}
                footerRight={committee.sessionId ? "Standing" : "Interim"}
                link={`/${
                  committee.sessionId
                    ? "standingCommittees"
                    : "nonStandingCommittees"
                }/${committee.id}`}
                footerLeftClass={getFooterLeftClass(committee)}
                footerRightClass={getFooterRightClass(committee)}
              />
            </MDBCardGroup>
          );
        })}
      </MDBRow>
      <MDBPagination className="mb-0 justify-content-center">
        {[
          ...Array(Math.ceil(filteredCommittees.length / itemsPerPage)).keys(),
        ].map((index) => (
          <MDBPaginationItem
            key={`page-${index}`}
            active={currentPage === index + 1}
          >
            <MDBPaginationLink onClick={() => handlePageChange(index + 1)}>
              {index + 1}
            </MDBPaginationLink>
          </MDBPaginationItem>
        ))}
      </MDBPagination>
    </>
  );
}

export default CommitteeList;
